import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CalendarRange, ArrowDownRight, ArrowUpRight, PiggyBank } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AnnualSnapshot({ refreshKey }) {
  const [a, setA] = useState(null);
  const year = new Date().getFullYear();
  useEffect(() => {
    api.get(`/snapbudget/annual?year=${year}`).then((r) => setA(r.data)).catch(() => {});
  }, [refreshKey, year]);

  if (!a) return null;
  const top = (a.by_category || []).filter((c) => c.amount > 0).slice(0, 5);
  const max = Math.max(...top.map((c) => c.amount), 1);

  return (
    <section data-testid="snap-annual" className="bg-[#0d1730] border border-emerald-500/20 rounded-2xl p-5 sm:p-6 shadow-2xl shadow-emerald-950/40">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold tracking-wide text-emerald-300 flex items-center gap-2">
          <CalendarRange className="w-5 h-5" /> {a.year} Snapshot
        </h2>
        <span className="text-xs text-slate-500">{a.months_active} active month(s)</span>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="rounded-xl border border-white/10 bg-[#0a1226]/70 p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><ArrowUpRight className="w-3 h-3 text-emerald-400" /> Income</p>
          <p data-testid="annual-income" className="font-mono text-lg text-emerald-400 mt-1">{money(a.income)}</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0a1226]/70 p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><ArrowDownRight className="w-3 h-3 text-rose-400" /> Spend</p>
          <p data-testid="annual-spend" className="font-mono text-lg text-rose-400 mt-1">{money(a.expenses)}</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0a1226]/70 p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><PiggyBank className="w-3 h-3 text-emerald-400" /> Saved</p>
          <p data-testid="annual-savings" className="font-mono text-lg text-emerald-300 mt-1">{money(a.savings)}</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0a1226]/70 p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Net</p>
          <p className={`font-mono text-lg mt-1 ${a.net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{money(a.net)}</p>
        </div>
      </div>

      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Top categories this year</p>
      {top.length === 0 ? (
        <p className="text-sm text-slate-500">No spending logged this year yet.</p>
      ) : (
        <div className="space-y-2">
          {top.map((c) => (
            <div key={c.key} className="flex items-center gap-3">
              <span className="w-20 shrink-0 text-xs text-slate-400 text-right">{c.label}</span>
              <div className="flex-1 h-4 bg-white/[0.04] rounded overflow-hidden">
                <div className="h-full bg-emerald-500/70 rounded" style={{ width: `${Math.round((c.amount / max) * 100)}%` }} />
              </div>
              <span className="w-20 shrink-0 text-[11px] font-mono text-right text-slate-300">{money(c.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
